/**
 * Utility functions for handling URL parameters in hash-based routing
 * This allows parameters to be placed after the hash fragment like:
 * https://example.com/author.html#/bulk-audio?courseIds=484&firmCode=devnathtest1&languages=es%2Cfr&autoLoad=true
 */

/**
 * Parse parameters from the hash fragment
 * Supports both formats:
 * - #/route?param=value (new format)
 * - ?param=value#/route (legacy format for backward compatibility)
 */
export function parseHashParams(): URLSearchParams {
  const hash = window.location.hash;
  const search = window.location.search;

  // Try new format first: #/route?param=value
  const hashMatch = hash.match(/#\/[^?]*\?(.+)/);
  if (hashMatch) {
    return new URLSearchParams(hashMatch[1]);
  }

  // Fallback to legacy format: ?param=value#/route
  if (search) {
    return new URLSearchParams(search);
  }

  return new URLSearchParams();
}

/**
 * Generate a URL with parameters in the hash fragment
 * @param route - The route path (e.g., '/bulk-audio')
 * @param params - The parameters to include
 * @returns The complete URL with hash-based parameters
 */
export function generateHashUrl(
  route: string,
  params: URLSearchParams
): string {
  const baseUrl = window.location.origin + window.location.pathname;
  const queryString = params.toString();

  if (queryString) {
    return `${baseUrl}#${route}?${queryString}`;
  } else {
    return `${baseUrl}#${route}`;
  }
}

/**
 * Update the current URL with new parameters in the hash
 * @param route - The route path (e.g., '/bulk-audio')
 * @param params - The parameters to include
 */
export function updateHashUrl(route: string, params: URLSearchParams): void {
  const newUrl = generateHashUrl(route, params);
  window.history.replaceState(null, '', newUrl);
}

/**
 * Get a specific parameter value from hash
 * @param key - The parameter key
 * @param defaultValue - Default value if parameter not found
 * @returns The parameter value or default
 */
export function getHashParam(key: string, defaultValue: string = ''): string {
  const params = parseHashParams();
  return params.get(key) || defaultValue;
}

/**
 * Set a parameter in the current hash URL
 * @param route - The current route
 * @param key - The parameter key
 * @param value - The parameter value
 */
export function setHashParam(route: string, key: string, value: string): void {
  const params = parseHashParams();
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
  updateHashUrl(route, params);
}

/**
 * Clear all parameters from the hash URL
 * @param route - The route to keep
 */
export function clearHashParams(route: string): void {
  updateHashUrl(route, new URLSearchParams());
}
